import { Injectable } from '@angular/core';
import { catchError, first, map } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import {
  PaymentExecutionV1Request,
  PaymentStatus,
  SerializableMap,
  TransactionAuthorizationResult,
} from '../models';
import { HttpErrorResponse } from '@angular/common/http';
import { getExecutePaymentErrorState } from '../helpers';
import { PaymentInformationServiceFactory } from '../factories';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private paymentInformationServiceFactory: PaymentInformationServiceFactory,
  ) {}

  executeTransactions(
    tenantId: string,
    paymentReferenceNumber: string,
    transactionReferences: string[],
    executePaymentRequest: PaymentExecutionV1Request,
    isScheduledPayment: boolean,
  ): Observable<SerializableMap<TransactionAuthorizationResult>> {
    const sources$ = transactionReferences.map((transactionReference) =>
      this.executePayment(
        tenantId,
        paymentReferenceNumber,
        transactionReference,
        executePaymentRequest,
        isScheduledPayment,
      ),
    );

    return forkJoin(sources$).pipe(
      map((results) => {
        const transactionAuthorizationResults: SerializableMap<TransactionAuthorizationResult> =
          {};

        results.forEach((result) => {
          transactionAuthorizationResults[result.transactionReference] =
            result.transactionResult;
        });

        return transactionAuthorizationResults;
      }),
    );
  }

  onCheckStatus(
    tenantId: string,
    masterConfirmationNumber: string,
    isScheduledPayment: boolean,
  ): Observable<SerializableMap<TransactionAuthorizationResult>> {
    return this.paymentInformationServiceFactory
      .create()
      .getPaymentByReferenceV1(tenantId, masterConfirmationNumber)
      .pipe(
        first(),
        map((response) => {
          const results = {};

          response.payments.forEach((transaction) => {
            const transactionResult: TransactionAuthorizationResult = {
              gpalStatus: transaction.paymentStatusText as PaymentStatus,
              paymentDate: transaction.paymentDate,
              isScheduledTransaction: isScheduledPayment,
            };

            results[transaction.externalPaymentId] = transactionResult;
          });

          return results;
        }),
      );
  }

  private executePayment(
    tenantId: string,
    paymentReference: string,
    transactionReference: string,
    request: PaymentExecutionV1Request,
    isScheduledPayment: boolean,
  ) {
    const transactionResult: TransactionAuthorizationResult = {
      isScheduledTransaction: isScheduledPayment,
    };
    const result = {
      transactionResult,
      transactionReference,
    };

    return this.paymentInformationServiceFactory
      .create()
      .executePaymentV1(
        tenantId,
        paymentReference,
        transactionReference,
        request,
      )
      .pipe(
        map((response) => {
          transactionResult.paymentDate = response.paymentDate;
          transactionResult.gpalStatus = response.status as PaymentStatus;

          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          const state = getExecutePaymentErrorState(error);
          transactionResult.gpalStatus = state.status;
          transactionResult.errorMessage = state.error;

          return of(result);
        }),
      );
  }
}
